import { AnyAction } from "redux"
import { ActionsObservable, StateObservable, combineEpics, Epic } from "redux-observable"
import { catchError } from "rxjs/operators"

// Models
import { EpicDependencies, RootState } from "../../models"

// Auth epics
import { fetchCurrentUserEpic, signInEpic, signOutEpic } from "../../modules/auth/store/epics"

// Doctor stats epics
import { fetchDoctorStatsEpic, fetchDoctorAvailableRecordsEpic, fetchInProgressRecordEpic } from "../../modules/doctor-stats/store/epics"

// Records epics
import {
    approveRecordEpic,
    fetchNextRecordEpic,
    fetchRecordByIdEpic,
    fetchRecordSignalEpic,
    sendUserInactiveEpic,
    updateRecordConclusionEpic,
    fetchNextByIdRecordEpic,
    fetchNextInProgressByIdRecordEpic,
    updateRecordParametersEpic,
    updateRecordInterpretationEpic,
    fetchPDFRecordEpic,
} from "../../modules/records/store/epics"

// WS epics
import {
    closeSingleRecordWSConnectionEpic,
    openSingleRecordConnectionEpic,
    runWSCustomPingEpic,
    runWSPingEpic,
    runListenerEpic,
    openAvailableRecordsConnectionEpic,
    closeAvailableRecordsWSConnectionEpic,
} from "../../modules/ws/store/epics"

/**
 * An unhandled throw anywhere inside an epic errors the merged output stream,
 * and `combineEpics` has no way to recover: RxJS unsubscribes EVERY epic, so
 * one bad dereference silently disables the whole application until reload —
 * no requests, no sign-in, no websockets, and no error visible to the user.
 *
 * Resubscribing keeps the rest of the app alive after such a throw. Individual
 * epics still handle their own expected failures with `catchError`; this is the
 * backstop for the unexpected ones only.
 *
 * @param epic
 */
const resilient = (epic: Epic<AnyAction, AnyAction, RootState, EpicDependencies>) =>
    (action$: ActionsObservable<AnyAction>, state$: StateObservable<RootState>, d: EpicDependencies) =>
        epic(action$, state$, d).pipe(
            catchError((error, source) => {
                // eslint-disable-next-line no-console
                console.error("Unhandled epic error — resubscribing", error);

                return source
            }),
        );

const rootEpic = combineEpics(
    // Auth
    signInEpic,
    signOutEpic,
    fetchCurrentUserEpic,
    // Doctor stats
    fetchDoctorStatsEpic,
    fetchInProgressRecordEpic,
    fetchDoctorAvailableRecordsEpic,
    // Records
    fetchPDFRecordEpic,
    fetchNextRecordEpic,
    fetchRecordByIdEpic,
    fetchRecordSignalEpic,
    approveRecordEpic,
    updateRecordConclusionEpic,
    sendUserInactiveEpic,
    fetchNextByIdRecordEpic,
    fetchNextInProgressByIdRecordEpic,
    updateRecordParametersEpic,
    updateRecordInterpretationEpic,
    // WS
    openSingleRecordConnectionEpic,
    closeSingleRecordWSConnectionEpic,
    runWSPingEpic,
    runListenerEpic,
    runWSCustomPingEpic,
    openAvailableRecordsConnectionEpic,
    closeAvailableRecordsWSConnectionEpic,
);

export default resilient(rootEpic as Epic<AnyAction, AnyAction, RootState, EpicDependencies>)
